import React from 'react';
import { ExternalLink, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ExamplesSection = () => {
  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const examples = [
    {
      title: 'Premium Auto Detailing',
      category: 'Car Detailing Studio',
      description: 'Modern website with online booking, before/after gallery, and automated review requests.',
      image: 'https://images.unsplash.com/photo-1507136566006-cfc505b114fc?w=800&auto=format&fit=crop&q=60',
    },
    {
      title: 'Prime Properties',
      category: 'Real Estate Agent',
      description: 'Property listings with virtual tours, lead capture forms, and automated follow-up sequences.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&auto=format&fit=crop&q=60',
    },
    {
      title: 'Express Clean',
      category: 'Dry Cleaner',
      description: 'Service listing with pricing, pickup scheduling, and automated status notifications.',
      image: 'https://images.unsplash.com/photo-1545173168-9f1947eebb7f?w=800&auto=format&fit=crop&q=60',
    },
    {
      title: 'Bright Smile Dental',
      category: 'Dental Clinic',
      description: 'Appointment booking, treatment showcases, patient testimonials, and reminder automation.',
      image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800&auto=format&fit=crop&q=60',
    },
    {
      title: 'Glow Beauty Studio',
      category: 'Salon',
      description: 'Service menu with online booking, stylist profiles, and automated appointment reminders.',
      image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800&auto=format&fit=crop&q=60',
    },
    {
      title: 'Excel Academy',
      category: 'Coaching Institute',
      description: 'Course catalog, batch schedules, student registration, and inquiry management system.',
      image: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800&auto=format&fit=crop&q=60',
    },
  ];

  return (
    <section 
      id="examples"
      className="py-24 lg:py-32 bg-slate-50"
      data-testid="examples-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-50 border border-violet-100 mb-6">
            <Eye className="w-4 h-4 text-violet-600" />
            <span className="text-sm font-medium text-violet-700">Portfolio</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Example Websites{' '}
            <span className="gradient-text">We've Built</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            See how we've helped different types of local businesses transform their online presence with AI-powered websites.
          </p>
        </div>

        {/* Examples Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {examples.map((example, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
              data-testid={`example-card-${index}`}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={example.image}
                  alt={example.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 portfolio-overlay opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                  <Button
                    variant="secondary"
                    className="rounded-full px-6 py-2 bg-white/90 backdrop-blur-sm hover:bg-white"
                    onClick={scrollToContact}
                    data-testid={`example-view-${index}`}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Request Demo
                  </Button>
                </div>
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="text-sm font-medium text-blue-600 mb-2">
                  {example.category}
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {example.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {example.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-slate-600 mb-4">
            Want a custom website for your business?
          </p>
          <Button
            onClick={() => {
              const element = document.querySelector('#contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="gradient-btn text-white rounded-full px-8 py-3 font-semibold"
            data-testid="examples-cta-button"
          >
            Request Your Free Demo
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ExamplesSection;
